import ExcelJS from 'exceljs';
import * as XLSX from 'xlsx';
import {
  OFFSHORE_EXCEL_HEADERS,
  OFFSHORE_ONBOARD_EXCEL_VALUES,
  OFFSHORE_SHIFT_EXCEL_VALUES,
  buildOffshoreDisplayName,
  isWelderJobDescription,
  normalizeRosterShift,
  parseOnboardValue,
} from './dprOffshorePersonnel';

const HEADER_ALIASES = {
  rosterId: ['id', '#'],
  jobDescription: ['job description', 'job desc', 'jobdescription', 'role', 'designation'],
  subJob: ['sub job', 'subjob', 'sub-job', 'sub role'],
  lastName: ['last name', 'lastname', 'surname'],
  firstName: ['first name', 'firstname', 'given name'],
  nationality: ['nationality', 'nation'],
  onboard: ['onboard', 'on board', 'on-board'],
  shift: ['shift'],
  station: ['station', 'stn', 'station #', 'station number'],
};

const normalizeHeaderCell = (cell) =>
  String(cell ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const findHeaderColumnIndex = (headers, aliases) => {
  for (let i = 0; i < headers.length; i += 1) {
    const h = normalizeHeaderCell(headers[i]);
    if (aliases.some((alias) => h === alias || h.includes(alias))) {
      return i;
    }
  }
  return -1;
};

const findHeaderRowIndex = (rows) => {
  for (let r = 0; r < Math.min(rows.length, 20); r += 1) {
    const row = rows[r];
    if (!Array.isArray(row)) continue;
    const idIdx = findHeaderColumnIndex(row, HEADER_ALIASES.rosterId);
    const jobIdx = findHeaderColumnIndex(row, HEADER_ALIASES.jobDescription);
    const lastIdx = findHeaderColumnIndex(row, HEADER_ALIASES.lastName);
    if (idIdx >= 0 && jobIdx >= 0 && lastIdx >= 0) {
      return r;
    }
  }
  return 0;
};

const buildColumnMap = (headerRow) => {
  const map = {};
  Object.entries(HEADER_ALIASES).forEach(([field, aliases]) => {
    map[field] = findHeaderColumnIndex(headerRow, aliases);
  });
  return map;
};

const cellAt = (row, index) => {
  if (index < 0 || index >= row.length) return '';
  const val = row[index];
  if (val === undefined || val === null) return '';
  return String(val).trim();
};

/**
 * Parse offshore personnel roster rows from an Excel sheet (8-column template or full Golfinho sheet).
 * Returns parsed row objects ready for preview / table replace.
 */
export const parseOffshorePersonnelExcelRows = (rows) => {
  if (!rows || rows.length < 2) {
    throw new Error('The uploaded file has no data rows.');
  }

  const headerRowIndex = findHeaderRowIndex(rows);
  const headerRow = rows[headerRowIndex];
  const col = buildColumnMap(headerRow);

  if (col.jobDescription < 0 || col.lastName < 0 || col.firstName < 0) {
    throw new Error(
      'Could not find required columns (ID, Job Description, Last Name, First Name). Check the template headers.'
    );
  }

  const parsed = [];
  for (let r = headerRowIndex + 1; r < rows.length; r += 1) {
    const row = rows[r];
    if (!Array.isArray(row)) continue;

    const jobDescription = cellAt(row, col.jobDescription);
    const subJob = cellAt(row, col.subJob);
    const lastName = cellAt(row, col.lastName);
    const firstName = cellAt(row, col.firstName);
    const station = cellAt(row, col.station);
    const rosterIdRaw = cellAt(row, col.rosterId);

    const hasContent = jobDescription || lastName || firstName || station || rosterIdRaw;
    if (!hasContent) continue;

    const rosterId = rosterIdRaw ? rosterIdRaw : parsed.length + 1;
    const onboard = parseOnboardValue(cellAt(row, col.onboard));
    const rosterShift = normalizeRosterShift(cellAt(row, col.shift));
    const nationality = cellAt(row, col.nationality);
    const name = buildOffshoreDisplayName(firstName, lastName);

    parsed.push({
      rosterId,
      jobDescription,
      subJob,
      lastName,
      firstName,
      nationality,
      onboard,
      rosterShift,
      allocatedStation: station,
      name,
      planned: 0,
      actual: 0,
      welderId: isWelderJobDescription(jobDescription) ? null : null,
      isNew: true,
    });
  }

  if (parsed.length === 0) {
    throw new Error('No personnel rows found in the uploaded file.');
  }

  return parsed;
};

export const readExcelRowsFromFile = async (file) => {
  const data = await file.arrayBuffer();
  const workbook = XLSX.read(data, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[sheetName];
  return XLSX.utils.sheet_to_json(worksheet, { header: 1 });
};

const formatOnboardForExcel = (onboard) => {
  if (onboard === true) return 'Yes';
  if (onboard === false) return 'No';
  return '';
};

const personnelRowToExcelValues = (row) => [
  row.rosterId ?? '',
  row.jobDescription ?? row.role ?? '',
  row.subJob ?? '',
  row.lastName ?? '',
  row.firstName ?? '',
  row.nationality ?? '',
  formatOnboardForExcel(row.onboard),
  normalizeRosterShift(row.rosterShift ?? row.shift ?? ''),
  row.allocatedStation ?? '',
];

const isExportablePersonnelRow = (row) => {
  if (!row || typeof row !== 'object') return false;
  return Boolean(
    String(row.rosterId ?? '').trim()
    || String(row.jobDescription ?? row.role ?? '').trim()
    || String(row.subJob ?? '').trim()
    || String(row.lastName ?? '').trim()
    || String(row.firstName ?? '').trim()
    || String(row.nationality ?? '').trim()
    || row.onboard === true
    || row.onboard === false
    || String(row.rosterShift ?? row.shift ?? '').trim()
    || String(row.allocatedStation ?? '').trim()
  );
};

const ONBOARD_COL_INDEX = OFFSHORE_EXCEL_HEADERS.indexOf('ONBOARD') + 1;
const SHIFT_COL_INDEX = OFFSHORE_EXCEL_HEADERS.indexOf('SHIFT') + 1;

const applyListColumnValidation = (worksheet, colIndex, values, rowStart = 2, rowEnd = 500) => {
  if (colIndex < 1 || !Array.isArray(values) || values.length === 0) return;
  const formula = `"${values.join(',')}"`;
  for (let row = rowStart; row <= rowEnd; row += 1) {
    worksheet.getCell(row, colIndex).dataValidation = {
      type: 'list',
      allowBlank: true,
      formulae: [formula],
      showErrorMessage: true,
      errorTitle: 'Invalid value',
      error: `Choose one of: ${values.join(', ')}`,
    };
  }
};

/** ONBOARD + SHIFT dropdowns on data rows (rows 2–500). */
export const applyOffshorePersonnelExcelValidations = (worksheet) => {
  applyListColumnValidation(worksheet, ONBOARD_COL_INDEX, OFFSHORE_ONBOARD_EXCEL_VALUES);
  applyListColumnValidation(worksheet, SHIFT_COL_INDEX, OFFSHORE_SHIFT_EXCEL_VALUES);
};

/** Download headers only when roster is empty; otherwise export current table values. */
export const downloadOffshorePersonnelTemplate = async (personnelRows = []) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Personnel Roster');

  worksheet.addRow(OFFSHORE_EXCEL_HEADERS);
  worksheet.getRow(1).font = { bold: true };
  OFFSHORE_EXCEL_HEADERS.forEach((title, index) => {
    worksheet.getColumn(index + 1).width = Math.max(14, String(title).length + 4);
  });

  (personnelRows || []).filter(isExportablePersonnelRow).forEach((row) => {
    worksheet.addRow(personnelRowToExcelValues(row));
  });

  applyOffshorePersonnelExcelValidations(worksheet);

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = personnelRows?.length
    ? 'offshore_personnel_roster.xlsx'
    : 'offshore_personnel_roster_template.xlsx';
  link.click();
  URL.revokeObjectURL(url);
};
